import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { RefreshCw, X, TrendingUp, TrendingDown, AlertCircle, ChevronDown, Activity, Bell, Plus, Pencil, Trash2 } from "lucide-react";
import {
  fetchTrades, closeTrade, fetchStockPortfolio, sellStockHolding, updateStockHolding,
  deleteStockHolding, buyStock, parseUTC,
} from "../api";
import type { Trade, MonitorSignal, StockHolding, StockPickCategory } from "../api";

// ─── Design tokens ────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<StockPickCategory, { label: string; color: string }> = {
  growth: { label: "Growth", color: "#38bdf8" },
  long_term: { label: "Long Term", color: "#818cf8" },
  value: { label: "Value", color: "#2dd4bf" },
  super_long_term: { label: "Super Long Term", color: "#c084fc" },
  moonshot: { label: "Moonshot", color: "#f472b6" },
};

const CATEGORIES: StockPickCategory[] = ["growth", "value", "long_term", "super_long_term", "moonshot"];

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatStrategy(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStrikes(trade: Trade): string {
  const isPut = trade.strategy === "bull_put_spread" || trade.strategy === "sell_put" || trade.strategy === "buy_put";
  const leg = isPut ? "P" : "C";
  if (trade.long_strike) {
    return `$${trade.short_strike}${leg} / $${trade.long_strike}${leg}`;
  }
  return `$${trade.short_strike}${leg}`;
}

function formatExpiry(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit", timeZone: "UTC" });
}

function formatDate(isoStr: string | null | undefined): string {
  if (!isoStr) return "—";
  return parseUTC(isoStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

function dteFromExpiry(expiryStr: string): number {
  const [y, m, d] = expiryStr.split("-").map(Number);
  const expiry = Date.UTC(y, m - 1, d);
  return Math.max(0, Math.round((expiry - Date.now()) / 86400000));
}

function pnlColor(val: number | undefined | null): string {
  if (val == null) return "#94a3b8";
  return val >= 0 ? "#4ade80" : "#f87171";
}

function signalConfig(signal: MonitorSignal | undefined): { label: string; bg: string; color: string } {
  switch (signal) {
    case "hold": return { label: "HOLD", bg: "#1e293b", color: "#94a3b8" };
    case "exit_profit": return { label: "TAKE PROFIT", bg: "#052e16", color: "#4ade80" };
    case "partial_exit_profit": return { label: "PARTIAL TAKE PROFIT", bg: "#052e16", color: "#4ade80" };
    case "roll": return { label: "ROLL", bg: "#451a03", color: "#fb923c" };
    case "exit_loss": return { label: "EXIT LOSS", bg: "#1a0a0a", color: "#f87171" };
    case "partial_reduce": return { label: "REDUCE POSITION", bg: "#451a03", color: "#fb923c" };
    case "approaching_expiry": return { label: "EXPIRING", bg: "#1c1200", color: "#facc15" };
    default: return { label: "—", bg: "#0d1526", color: "#475569" };
  }
}

function stockSignalConfig(signal: string): { label: string; bg: string; color: string } {
  switch (signal) {
    case "hold": return { label: "HOLD", bg: "#1e293b", color: "#94a3b8" };
    case "sell": return { label: "SELL", bg: "#1a0a0a", color: "#f87171" };
    case "strong_sell": return { label: "STRONG SELL", bg: "#1a0a0a", color: "#f87171" };
    default: return { label: signal.toUpperCase(), bg: "#0d1526", color: "#475569" };
  }
}

function statusConfig(status: string): { label: string; color: string } {
  switch (status) {
    case "open": return { label: "OPEN", color: "#60a5fa" };
    case "closed_profit": return { label: "PROFIT", color: "#4ade80" };
    case "closed_loss": return { label: "LOSS", color: "#f87171" };
    case "expired_worthless": return { label: "EXPIRED", color: "#4ade80" };
    case "assigned": return { label: "ASSIGNED", color: "#fb923c" };
    case "rolled": return { label: "ROLLED", color: "#facc15" };
    default: return { label: status.toUpperCase(), color: "#94a3b8" };
  }
}

// ─── Source Badge ──────────────────────────────────────────────────────────

function SourceBadge({ isAgent }: { isAgent: boolean }) {
  return (
    <span style={{
      fontSize: 9, fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.5,
      padding: "3px 7px", borderRadius: 4,
      background: isAgent ? "#1d4ed815" : "#47556915",
      color: isAgent ? "#60a5fa" : "#64748b",
    }}>
      {isAgent ? "AGENT" : "MANUAL"}
    </span>
  );
}

// ─── NeedsAttentionBanner ───────────────────────────────────────────────────

function NeedsAttentionBanner({ trades, holdings }: { trades: Trade[]; holdings: StockHolding[] }) {
  const actionableTrades = trades.filter(
    (t) => t.status === "open" && t.latest_signal && t.latest_signal !== "hold"
  );
  const actionableHoldings = holdings.filter(
    (h) => h.status === "holding" && (h.signal === "sell" || h.signal === "strong_sell")
  );

  const totalActionable = actionableTrades.length + actionableHoldings.length;
  if (totalActionable === 0) return null;

  return (
    <div style={{
      background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
      padding: "16px 20px", marginBottom: 24,
      display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      <Bell size={18} color="#f87171" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f87171", marginBottom: 8 }}>
          {totalActionable} position{totalActionable > 1 ? "s" : ""} need{totalActionable === 1 ? "s" : ""} attention
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {actionableTrades.map((t) => {
            const sig = signalConfig(t.latest_signal);
            return (
              <div key={`opt-${t.id}`} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: sig.bg, border: `1px solid ${sig.color}30`,
                borderRadius: 8, padding: "5px 12px",
              }}>
                <span style={{ fontSize: 9, color: "#475569", fontFamily: "monospace" }}>OPT</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#f1f5f9", fontWeight: 700 }}>
                  {t.ticker}
                </span>
                <span style={{ fontSize: 10, color: sig.color, fontWeight: 700, fontFamily: "monospace" }}>
                  {sig.label}
                </span>
              </div>
            );
          })}
          {actionableHoldings.map((h) => {
            const sig = stockSignalConfig(h.signal);
            return (
              <div key={`stk-${h.id}`} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: sig.bg, border: `1px solid ${sig.color}30`,
                borderRadius: 8, padding: "5px 12px",
              }}>
                <span style={{ fontSize: 9, color: "#475569", fontFamily: "monospace" }}>STK</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#f1f5f9", fontWeight: 700 }}>
                  {h.ticker}
                </span>
                <span style={{ fontSize: 10, color: sig.color, fontWeight: 700, fontFamily: "monospace" }}>
                  {sig.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── PnLBar ────────────────────────────────────────────────────────────────

function PnLBar({ pnlPct }: { pnlPct: number | undefined | null }) {
  if (pnlPct == null) return null;
  const pct = Math.min(100, Math.max(0, Math.abs(pnlPct) * 100));
  const isPos = pnlPct >= 0;
  const color = pct >= 75 ? "#4ade80" : pct >= 40 ? "#facc15" : isPos ? "#60a5fa" : "#f87171";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
      <div style={{ width: 60, height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 10, color, fontFamily: "monospace" }}>{pct.toFixed(0)}%</span>
    </div>
  );
}

// ─── CloseModal (Options) ──────────────────────────────────────────────────

interface CloseModalProps {
  trade: Trade;
  mode: "close" | "expire";
  onClose: () => void;
  onSuccess: () => void;
}

function CloseModal({ trade, mode, onClose, onSuccess }: CloseModalProps) {
  const isBuy = isBuyStrategy(trade.strategy);
  const [debit, setDebit] = useState(mode === "expire" ? "0" : "");
  const [closeQty, setCloseQty] = useState(
    trade.suggested_close_qty && trade.suggested_close_qty < trade.quantity
      ? trade.suggested_close_qty
      : trade.quantity
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPartial = closeQty < trade.quantity;
  const closePrice = parseFloat(debit) || 0;

  // Preview P&L for the number of contracts being closed
  let previewPnl: number | null = null;
  if (mode === "expire") {
    previewPnl = trade.net_credit * 100 * closeQty;
  } else if (debit !== "") {
    previewPnl = isBuy
      ? (closePrice + trade.net_credit) * 100 * closeQty
      : (trade.net_credit - closePrice) * 100 * closeQty;
  }

  async function handleSubmit() {
    setLoading(true);
    setError(null);
    try {
      await closeTrade(trade.id, {
        action: mode,
        close_debit: closePrice,
        close_quantity: closeQty,
      });
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to close trade");
    } finally {
      setLoading(false);
    }
  }

  const title = mode === "expire"
    ? "Expire Worthless"
    : isPartial
      ? `Close ${closeQty} of ${trade.quantity}`
      : "Close Trade";
  const desc = mode === "expire"
    ? isBuy
      ? "Mark this trade as expired worthless (total premium lost)."
      : "Mark this trade as expired worthless (full premium captured)."
    : isBuy
      ? "Enter the price you sold to close (credit received)."
      : "Enter the debit paid to close the position.";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(5,12,24,0.85)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16,
        padding: 28, width: "100%", maxWidth: 420,
        animation: "slideUp 0.2s ease",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: "#f1f5f9" }}>
              {title}
            </div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 4, fontFamily: "monospace" }}>
              {trade.ticker} · {formatStrikes(trade)} · {trade.quantity} contract{trade.quantity > 1 ? "s" : ""}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: "#475569", cursor: "pointer",
          }}>
            <X size={18} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20, lineHeight: 1.6 }}>{desc}</p>

        {/* Quantity selector — only show if trade has > 1 contract */}
        {trade.quantity > 1 && (
          <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>
              CONTRACTS TO {mode === "expire" ? "EXPIRE" : "CLOSE"}
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="number"
                min={1}
                max={trade.quantity}
                step={1}
                value={closeQty}
                onChange={(e) => {
                  const v = parseInt(e.target.value) || 1;
                  setCloseQty(Math.min(Math.max(1, v), trade.quantity));
                }}
                style={{
                  flex: 1, background: "#0d1526", border: "1px solid #1e293b", borderRadius: 8,
                  padding: "10px 14px", color: "#e2e8f0", fontSize: 15,
                  fontFamily: "'Space Mono', monospace",
                }}
              />
              {closeQty < trade.quantity && (
                <button onClick={() => setCloseQty(trade.quantity)} style={{
                  padding: "10px 14px", background: "#1e293b", border: "none", borderRadius: 8,
                  color: "#94a3b8", fontSize: 12, fontFamily: "monospace", cursor: "pointer",
                  whiteSpace: "nowrap",
                }}>ALL</button>
              )}
            </div>
            {isPartial && (
              <span style={{ fontSize: 11, color: "#60a5fa", fontFamily: "monospace" }}>
                Partial close: {closeQty} of {trade.quantity} contracts · {trade.quantity - closeQty} will remain open
              </span>
            )}
            {trade.suggested_close_qty && trade.suggested_close_qty < trade.quantity && (
              <div style={{
                background: "#1d4ed810", border: "1px solid #1d4ed830", borderRadius: 6,
                padding: "6px 10px", marginTop: 4,
              }}>
                <span style={{ fontSize: 10, color: "#60a5fa", fontFamily: "monospace" }}>
                  AGENT SUGGESTS: Close {trade.suggested_close_qty} of {trade.quantity}
                  {trade.partial_close_reasoning && (
                    <span style={{ color: "#475569" }}> — {trade.partial_close_reasoning}</span>
                  )}
                </span>
              </div>
            )}
          </label>
        )}

        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1 }}>
            {isBuy ? "CLOSE PRICE (PER SHARE)" : "CLOSE DEBIT (PER SHARE)"}
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={debit}
            onChange={(e) => setDebit(e.target.value)}
            readOnly={mode === "expire"}
            style={{
              background: "#0d1526", border: "1px solid #1e293b", borderRadius: 8,
              padding: "10px 14px", color: "#e2e8f0", fontSize: 15,
              fontFamily: "'Space Mono', monospace", width: "100%",
              opacity: mode === "expire" ? 0.5 : 1,
            }}
          />
          {mode !== "expire" && (
            <span style={{ fontSize: 11, color: "#334155", fontFamily: "monospace" }}>
              {isBuy
                ? `Premium paid: $${Math.abs(trade.net_credit).toFixed(2)} · Enter sale price to calculate P&L`
                : `Credit received: $${trade.net_credit.toFixed(2)} · Enter 0 for full profit`}
            </span>
          )}
        </label>

        {/* P&L Preview */}
        {previewPnl !== null && (
          <div style={{
            background: "#050c18",
            border: `1px solid ${previewPnl >= 0 ? "#166534" : "#7f1d1d"}`,
            borderRadius: 8, padding: 14, marginBottom: 16,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
              {isPartial ? `P&L (${closeQty} contracts)` : "REALIZED P&L"}
            </span>
            <span style={{
              fontSize: 18, fontWeight: 700, fontFamily: "'Space Mono', monospace",
              color: previewPnl >= 0 ? "#4ade80" : "#f87171",
            }}>
              {previewPnl >= 0 ? "+" : ""}${previewPnl.toFixed(0)}
            </span>
          </div>
        )}

        {error && (
          <div style={{
            background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 8,
            padding: "10px 14px", marginBottom: 14, fontSize: 12,
            color: "#f87171", fontFamily: "monospace",
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "10px 0", borderRadius: 8,
            border: "1px solid #1e293b", background: "transparent",
            color: "#475569", fontSize: 13, cursor: "pointer",
          }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} style={{
            flex: 2, padding: "10px 0", borderRadius: 8,
            border: loading ? "1px solid #1e293b" : mode === "expire" ? (isBuy ? "1px solid #7f1d1d" : "1px solid #166534") : "1px solid transparent",
            background: loading ? "#1e293b" : mode === "expire" ? (isBuy ? "#1a0a0a" : "#052e16") : "#1d4ed8",
            color: loading ? "#475569" : mode === "expire" ? (isBuy ? "#f87171" : "#4ade80") : "#fff",
            fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            {loading ? (
              <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Processing...</>
            ) : (
              title
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SellModal (Stocks) ──────────────────────────────────────────────────────

function SellModal({ holding, onClose, onSell }: {
  holding: StockHolding;
  onClose: () => void;
  onSell: () => void;
}) {
  const [sellPrice, setSellPrice] = useState(holding.current_price ?? holding.buy_price);
  const [sellQuantity, setSellQuantity] = useState(holding.quantity);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pnl = (sellPrice - holding.buy_price) * sellQuantity;
  const pnlPct = ((sellPrice - holding.buy_price) / holding.buy_price) * 100;

  async function handleSell() {
    if (sellQuantity <= 0 || sellQuantity > holding.quantity) return;
    setSubmitting(true);
    try {
      await sellStockHolding(holding.id, { sell_price: sellPrice, quantity: sellQuantity, notes: notes || undefined });
      onSell();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16, padding: 28, width: 420,
        animation: "fadeIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#f87171", fontFamily: "monospace", letterSpacing: 1 }}>SELL STOCK</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginTop: 4 }}>
              {holding.ticker} <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 400 }}>x{holding.quantity}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16,
          background: "#050c18", borderRadius: 8, padding: 12,
        }}>
          <div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace" }}>BUY PRICE</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>${holding.buy_price.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace" }}>COST BASIS</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>${holding.total_cost.toFixed(2)}</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>SELL PRICE</label>
          <input
            type="number" step="0.01" value={sellPrice} onChange={e => setSellPrice(parseFloat(e.target.value) || 0)}
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>QUANTITY (of {holding.quantity})</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number" min={1} max={holding.quantity} step={1} value={sellQuantity}
              onChange={e => {
                const v = parseFloat(e.target.value) || 0;
                setSellQuantity(Math.min(v, holding.quantity));
              }}
              style={{
                flex: 1, padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
                color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
              }}
            />
            {sellQuantity < holding.quantity && (
              <button onClick={() => setSellQuantity(holding.quantity)} style={{
                padding: "10px 14px", background: "#1e293b", border: "none", borderRadius: 8,
                color: "#94a3b8", fontSize: 12, fontFamily: "monospace", cursor: "pointer",
              }}>ALL</button>
            )}
          </div>
        </div>

        <div style={{
          background: "#050c18", border: `1px solid ${pnl >= 0 ? "#166534" : "#7f1d1d"}`, borderRadius: 8, padding: 14,
          marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>REALIZED P&L</span>
          <span style={{
            fontSize: 18, fontWeight: 700, fontFamily: "'Space Mono', monospace",
            color: pnl >= 0 ? "#4ade80" : "#f87171",
          }}>
            {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
          </span>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>NOTES (OPTIONAL)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Taking profits after earnings"
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#94a3b8", fontSize: 13,
            }}
          />
        </div>

        <button onClick={handleSell} disabled={submitting} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: pnl >= 0 ? "linear-gradient(135deg, #166534 0%, #22c55e 100%)" : "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
          color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif",
        }}>
          {submitting ? "RECORDING..." : `RECORD SALE (${pnl >= 0 ? "PROFIT" : "LOSS"})`}
        </button>
      </div>
    </div>
  );
}

// ─── ManualAddModal (Stocks) ──────────────────────────────────────────────────

function ManualAddModal({ onClose, onAdd }: {
  onClose: () => void;
  onAdd: () => void;
}) {
  const [ticker, setTicker] = useState("");
  const [category, setCategory] = useState<StockPickCategory>("long_term");
  const [avgPrice, setAvgPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [isAgentPick, setIsAgentPick] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const price = parseFloat(avgPrice) || 0;
  const totalCost = price * quantity;

  async function handleAdd() {
    const trimmedTicker = ticker.trim().toUpperCase();
    if (!trimmedTicker || !/^[A-Z]{1,5}$/.test(trimmedTicker)) {
      setError("Enter a valid ticker (1-5 letters)");
      return;
    }
    if (price <= 0) {
      setError("Enter a valid average price");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await buyStock({
        ticker: trimmedTicker,
        category,
        buy_price: price,
        quantity,
        notes: notes || undefined,
        is_agent_pick: isAgentPick,
      });
      onAdd();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16, padding: 28, width: 420,
        animation: "fadeIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#60a5fa", fontFamily: "monospace", letterSpacing: 1 }}>ADD STOCK</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginTop: 4 }}>
              Record a stock you already own
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div style={{
            background: "#7f1d1d20", border: "1px solid #7f1d1d", borderRadius: 8, padding: "8px 12px",
            marginBottom: 14, fontSize: 12, color: "#f87171",
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>TICKER</label>
          <input
            value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())} placeholder="e.g. AAPL"
            maxLength={5}
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace", textTransform: "uppercase",
            }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>CATEGORY</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CATEGORIES.map(cat => {
              const cfg = CATEGORY_CONFIG[cat];
              const active = category === cat;
              return (
                <button key={cat} onClick={() => setCategory(cat)} style={{
                  padding: "6px 12px", borderRadius: 8,
                  border: `1px solid ${active ? cfg.color + "50" : "#1e293b"}`,
                  background: active ? cfg.color + "15" : "#050c18",
                  color: active ? cfg.color : "#475569",
                  fontSize: 11, fontWeight: active ? 700 : 500, cursor: "pointer",
                  fontFamily: "monospace",
                }}>
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>AVG PRICE</label>
            <input
              type="number" step="0.01" min="0.01" value={avgPrice} onChange={e => setAvgPrice(e.target.value)}
              placeholder="0.00"
              style={{
                width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
                color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>SHARES</label>
            <input
              type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              style={{
                width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
                color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
              }}
            />
          </div>
        </div>

        <div style={{
          background: "#050c18", border: "1px solid #1e293b", borderRadius: 8, padding: 14, marginBottom: 14,
          display: "flex", justifyContent: "space-between",
        }}>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>TOTAL COST</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
            ${totalCost.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>

        <label style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 14, cursor: "pointer",
          padding: "10px 14px", background: isAgentPick ? "#1d4ed810" : "#050c18",
          border: `1px solid ${isAgentPick ? "#1d4ed850" : "#1e293b"}`, borderRadius: 8,
        }} onClick={() => setIsAgentPick(!isAgentPick)}>
          <div style={{
            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
            border: `2px solid ${isAgentPick ? "#60a5fa" : "#334155"}`,
            background: isAgentPick ? "#1d4ed8" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s ease",
          }}>
            {isAgentPick && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div>
            <div style={{ fontSize: 12, color: isAgentPick ? "#60a5fa" : "#94a3b8", fontWeight: 600, fontFamily: "monospace" }}>
              Agent recommended
            </div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginTop: 2 }}>
              Include in self-learning pipeline for scoring improvements
            </div>
          </div>
        </label>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>NOTES (OPTIONAL)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Bought on Robinhood Jan 2025"
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#94a3b8", fontSize: 13,
            }}
          />
        </div>

        <button onClick={handleAdd} disabled={submitting} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
          color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <Plus size={16} />
          {submitting ? "ADDING..." : "ADD TO PORTFOLIO"}
        </button>
      </div>
    </div>
  );
}

// ─── EditHoldingModal (Stocks) ────────────────────────────────────────────────

function EditHoldingModal({ holding, onClose, onSave }: {
  holding: StockHolding;
  onClose: () => void;
  onSave: () => void;
}) {
  const [buyPrice, setBuyPrice] = useState(holding.buy_price.toString());
  const [quantity, setQuantity] = useState(holding.quantity);
  const [category, setCategory] = useState<StockPickCategory>(holding.category as StockPickCategory);
  const [isAgentPick, setIsAgentPick] = useState(!!holding.is_agent_pick);
  const [notes, setNotes] = useState(holding.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const price = parseFloat(buyPrice) || 0;

  async function handleSave() {
    if (price <= 0) { setError("Enter a valid price"); return; }
    if (quantity <= 0) { setError("Enter a valid quantity"); return; }
    setSubmitting(true);
    setError(null);
    try {
      await updateStockHolding(holding.id, {
        buy_price: price,
        quantity,
        category,
        notes: notes || undefined,
        is_agent_pick: isAgentPick,
      });
      onSave();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "#0a1628", border: "1px solid #1e293b", borderRadius: 16, padding: 28, width: 420,
        animation: "fadeIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#facc15", fontFamily: "monospace", letterSpacing: 1 }}>EDIT HOLDING</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginTop: 4 }}>{holding.ticker}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div style={{
            background: "#7f1d1d20", border: "1px solid #7f1d1d", borderRadius: 8, padding: "8px 12px",
            marginBottom: 14, fontSize: 12, color: "#f87171",
          }}>{error}</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>CATEGORY</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CATEGORIES.map(cat => {
              const cfg = CATEGORY_CONFIG[cat];
              const active = category === cat;
              return (
                <button key={cat} onClick={() => setCategory(cat)} style={{
                  padding: "6px 12px", borderRadius: 8,
                  border: `1px solid ${active ? cfg.color + "50" : "#1e293b"}`,
                  background: active ? cfg.color + "15" : "#050c18",
                  color: active ? cfg.color : "#475569",
                  fontSize: 11, fontWeight: active ? 700 : 500, cursor: "pointer", fontFamily: "monospace",
                }}>{cfg.label}</button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>AVG PRICE</label>
            <input
              type="number" step="0.01" min="0.01" value={buyPrice} onChange={e => setBuyPrice(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
                color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
              }}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>SHARES</label>
            <input
              type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              style={{
                width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
                color: "#f1f5f9", fontSize: 16, fontFamily: "'Space Mono', monospace",
              }}
            />
          </div>
        </div>

        <div style={{
          background: "#050c18", border: "1px solid #1e293b", borderRadius: 8, padding: 14, marginBottom: 14,
          display: "flex", justifyContent: "space-between",
        }}>
          <span style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>TOTAL COST</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", fontFamily: "'Space Mono', monospace" }}>
            ${(price * quantity).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>

        <label style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 14, cursor: "pointer",
          padding: "10px 14px", background: isAgentPick ? "#1d4ed810" : "#050c18",
          border: `1px solid ${isAgentPick ? "#1d4ed850" : "#1e293b"}`, borderRadius: 8,
        }} onClick={() => setIsAgentPick(!isAgentPick)}>
          <div style={{
            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
            border: `2px solid ${isAgentPick ? "#60a5fa" : "#334155"}`,
            background: isAgentPick ? "#1d4ed8" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.15s ease",
          }}>
            {isAgentPick && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div>
            <div style={{ fontSize: 12, color: isAgentPick ? "#60a5fa" : "#94a3b8", fontWeight: 600, fontFamily: "monospace" }}>
              Agent recommended
            </div>
            <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginTop: 2 }}>
              Include in self-learning pipeline
            </div>
          </div>
        </label>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 11, color: "#475569", fontFamily: "monospace", marginBottom: 6 }}>NOTES (OPTIONAL)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Averaged down"
            style={{
              width: "100%", padding: "10px 14px", background: "#050c18", border: "1px solid #1e293b", borderRadius: 8,
              color: "#94a3b8", fontSize: 13,
            }}
          />
        </div>

        <button onClick={handleSave} disabled={submitting} style={{
          width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
          background: "linear-gradient(135deg, #854d0e 0%, #facc15 100%)",
          color: "#0a1628", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <Pencil size={14} />
          {submitting ? "SAVING..." : "SAVE CHANGES"}
        </button>
      </div>
    </div>
  );
}

// ─── DeleteHoldingModal (Stocks) ──────────────────────────────────────────────

function DeleteHoldingModal({ holding, onClose, onDelete }: {
  holding: StockHolding;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    setSubmitting(true);
    try {
      await deleteStockHolding(holding.id);
      onDelete();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div style={{
        background: "#0a1628", border: "1px solid #7f1d1d", borderRadius: 16, padding: 28, width: 400,
        animation: "fadeIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: "#f87171", fontFamily: "monospace", letterSpacing: 1 }}>DELETE HOLDING</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#f1f5f9", marginTop: 4 }}>{holding.ticker}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{
          background: "#7f1d1d15", border: "1px solid #7f1d1d40", borderRadius: 8, padding: 14, marginBottom: 20,
          fontSize: 13, color: "#94a3b8", lineHeight: 1.6,
        }}>
          This will permanently remove <span style={{ color: "#f1f5f9", fontWeight: 700 }}>{holding.quantity} shares of {holding.ticker}</span> (avg ${holding.buy_price.toFixed(2)}) from your portfolio. This cannot be undone.
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #1e293b",
            background: "transparent", color: "#94a3b8", fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>
            Cancel
          </button>
          <button onClick={handleDelete} disabled={submitting} style={{
            flex: 1, padding: "12px 0", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)",
            color: "#fff", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <Trash2 size={14} />
            {submitting ? "DELETING..." : "DELETE"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ActionsDropdown (Options) ────────────────────────────────────────────────

interface ActionsDropdownProps {
  trade: Trade;
  onAction: (trade: Trade, mode: "close" | "expire") => void;
}

function ActionsDropdown({ trade, onAction }: ActionsDropdownProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
  }, []);

  useEffect(() => {
    if (open) {
      updatePos();
      window.addEventListener("scroll", updatePos, true);
      return () => window.removeEventListener("scroll", updatePos, true);
    }
  }, [open, updatePos]);

  return (
    <div style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "5px 12px", borderRadius: 6, border: "1px solid #1e293b",
          background: "#0d1526", color: "#64748b", fontSize: 11,
          cursor: "pointer", fontFamily: "monospace",
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        Actions <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 100 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "fixed", top: pos.top, left: pos.left, zIndex: 101,
            background: "#0a1628", border: "1px solid #1e293b", borderRadius: 10,
            overflow: "hidden", minWidth: 160,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          }}>
            <button
              onClick={() => { setOpen(false); onAction(trade, "close"); }}
              style={{
                display: "block", width: "100%", padding: "10px 14px",
                background: "transparent", border: "none", textAlign: "left",
                color: trade.latest_signal === "exit_profit" ? "#4ade80" : "#e2e8f0",
                fontSize: 12, cursor: "pointer", fontFamily: "monospace",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = trade.latest_signal === "exit_profit" ? "#052e16" : "#0d1526")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {trade.latest_signal === "exit_profit"
                ? (trade.quantity > 1 ? "✓ Close (Full or Partial)" : "✓ Close for Profit")
                : (trade.quantity > 1 ? "Close (Full or Partial)" : "Close Trade")}
            </button>
            {trade.suggested_close_qty && trade.suggested_close_qty > 0 && trade.suggested_close_qty < trade.quantity && (
              <button
                onClick={() => { setOpen(false); onAction(trade, "close"); }}
                style={{
                  display: "block", width: "100%", padding: "10px 14px",
                  background: "transparent", border: "none", textAlign: "left",
                  color: "#60a5fa", fontSize: 12, cursor: "pointer", fontFamily: "monospace",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                Agent Suggests: Close {trade.suggested_close_qty} of {trade.quantity}
              </button>
            )}
            <button
              onClick={() => { setOpen(false); onAction(trade, "expire"); }}
              style={{
                display: "block", width: "100%", padding: "10px 14px",
                background: "transparent", border: "none", textAlign: "left",
                color: "#94a3b8", fontSize: 12, cursor: "pointer", fontFamily: "monospace",
                borderTop: "1px solid #1e293b",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              Expire Worthless
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── PortfolioBar ──────────────────────────────────────────────────────────

function PortfolioBar({ trades, holdings }: { trades: Trade[]; holdings: StockHolding[] }) {
  const openTrades = trades.filter((t) => t.status === "open");
  const activeHoldings = holdings.filter((h) => h.status === "holding");
  const totalPositions = openTrades.length + activeHoldings.length;

  const hasOptionsPnl = openTrades.some((t) => t.unrealized_pnl != null);
  const optionsPnl = openTrades.reduce((sum, t) => sum + (t.unrealized_pnl ?? 0), 0);
  const stocksPnl = activeHoldings.reduce((sum, h) => {
    const cp = h.current_price ?? h.buy_price;
    return sum + (cp - h.buy_price) * h.quantity;
  }, 0);
  const totalUnrealized = optionsPnl + stocksPnl;
  const hasPnl = hasOptionsPnl || activeHoldings.length > 0;

  const totalContracts = openTrades.reduce((s, t) => s + t.quantity, 0);
  const totalShares = activeHoldings.reduce((s, h) => s + h.quantity, 0);

  const stocksMarketValue = activeHoldings.reduce((sum, h) => {
    const cp = h.current_price ?? h.buy_price;
    return sum + cp * h.quantity;
  }, 0);
  const stocksCost = activeHoldings.reduce((sum, h) => sum + h.total_cost, 0);

  const stats = [
    {
      icon: <Activity size={15} />,
      label: "OPEN POSITIONS",
      value: totalPositions.toString(),
      sub: `${openTrades.length} options · ${activeHoldings.length} stocks`,
      color: "#60a5fa",
    },
    {
      icon: <TrendingUp size={15} />,
      label: "STOCK MARKET VALUE",
      value: stocksMarketValue > 0 ? `$${stocksMarketValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—",
      sub: stocksCost > 0 ? `Cost basis: $${stocksCost.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : undefined,
      color: "#60a5fa",
    },
    {
      icon: totalUnrealized >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />,
      label: "UNREALIZED P&L",
      value: hasPnl ? `${totalUnrealized >= 0 ? "+" : ""}$${totalUnrealized.toFixed(0)}` : (openTrades.length > 0 ? "Pending" : "—"),
      sub: hasPnl
        ? `Opt: ${hasOptionsPnl ? `${optionsPnl >= 0 ? "+" : ""}$${optionsPnl.toFixed(0)}` : "pending"} · Stk: ${stocksPnl >= 0 ? "+" : ""}$${stocksPnl.toFixed(0)}`
        : (openTrades.length > 0 ? "Awaiting next monitoring cycle" : undefined),
      color: hasPnl ? (totalUnrealized >= 0 ? "#4ade80" : "#f87171") : "#475569",
    },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
      {stats.map((s) => (
        <div key={s.label} style={{
          background: "#0a1628", border: "1px solid #1e293b", borderRadius: 12,
          padding: "16px 20px", display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{ color: s.color }}>{s.icon}</div>
          <div>
            <div style={{ fontSize: 9, color: "#475569", letterSpacing: 1, marginBottom: 3, fontFamily: "monospace" }}>
              {s.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: "'Space Mono', monospace" }}>
              {s.value}
            </div>
            {s.sub && (
              <div style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginTop: 2 }}>
                {s.sub}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── OptionsTable ──────────────────────────────────────────────────────────

const COL_STYLE: React.CSSProperties = {
  padding: "12px 16px",
  fontSize: 12,
  fontFamily: "monospace",
  borderBottom: "1px solid #0d1526",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const COL_RIGHT: React.CSSProperties = {
  ...COL_STYLE,
  textAlign: "right",
};

const RIGHT_COLS = new Set(["DTE", "Premium", "P&L", "P&L%", "Qty", "Avg Price", "Current", "Value", "Credit", "Buy", "Sell", "Return", "Price"]);

interface OptionsTableProps {
  trades: Trade[];
  showActions: boolean;
  onAction: (trade: Trade, mode: "close" | "expire") => void;
}

function isBuyStrategy(strategy: string): boolean {
  return strategy === "buy_call" || strategy === "buy_put";
}

function OptionsTable({ trades, showActions, onAction }: OptionsTableProps) {
  if (trades.length === 0) return null;

  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Strategy", "Source", "Qty", "Strikes", "Expiry", "DTE", "Premium", "P&L", "Signal", ...(showActions ? ["Actions"] : [])].map((h) => (
              <th key={h} style={{
                ...COL_STYLE,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: RIGHT_COLS.has(h) ? "right" : "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((trade, i) => {
            const sig = signalConfig(trade.latest_signal);
            const dte = trade.days_remaining ?? dteFromExpiry(trade.short_expiry);
            const dteColor = dte <= 7 ? "#f87171" : dte <= 14 ? "#facc15" : "#94a3b8";
            const pnl = trade.unrealized_pnl ?? trade.realized_pnl;
            const pnlDisplay = pnl != null
              ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}`
              : "Pending";
            const isBuy = isBuyStrategy(trade.strategy);
            const premiumLabel = isBuy ? `-$${Math.abs(trade.net_credit).toFixed(2)}` : `$${trade.net_credit.toFixed(2)}`;
            const premiumColor = isBuy ? "#f87171" : "#4ade80";

            const rowBg = trade.latest_signal === "exit_loss"
              ? "#150808"
              : trade.latest_signal === "exit_profit"
              ? "#071510"
              : trade.latest_signal === "roll" || trade.latest_signal === "approaching_expiry"
              ? "#130e03"
              : i % 2 === 0 ? "#0a1628" : "#080f1e";

            return (
              <tr
                key={trade.id}
                style={{ background: rowBg }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
              >
                <td style={COL_STYLE}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {trade.ticker}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#94a3b8" }}>{formatStrategy(trade.strategy)}</span>
                </td>
                <td style={COL_STYLE}>
                  <SourceBadge isAgent={!!trade.is_agent_recommended} />
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    {trade.original_quantity && trade.original_quantity !== trade.quantity
                      ? <>{trade.quantity}<span style={{ color: "#475569", fontSize: 10 }}>/{trade.original_quantity}</span></>
                      : trade.quantity}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    {formatStrikes(trade)}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#64748b" }}>{formatExpiry(trade.short_expiry)}</span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: dteColor, fontFamily: "'Space Mono', monospace", fontWeight: dte <= 7 ? 700 : 400 }}>
                    {dte}d
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 4 }}>
                    <span style={{ color: premiumColor, fontFamily: "'Space Mono', monospace" }}>
                      {premiumLabel}
                    </span>
                    {isBuy && (
                      <span style={{ fontSize: 9, color: "#475569", fontFamily: "monospace" }}>DEBIT</span>
                    )}
                  </div>
                </td>
                <td style={COL_RIGHT}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                    <PnLBar pnlPct={trade.pnl_pct} />
                    <span style={{ color: pnl != null ? pnlColor(pnl) : "#475569", fontFamily: "'Space Mono', monospace", fontWeight: 600, fontStyle: pnl == null ? "italic" : "normal", fontSize: pnl == null ? 10 : 12 }}>
                      {pnlDisplay}
                    </span>
                  </div>
                </td>
                <td style={{ ...COL_STYLE, whiteSpace: "normal", maxWidth: 240 }}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10,
                    fontWeight: 700, letterSpacing: 0.5,
                    background: sig.bg, color: sig.color,
                  }}>
                    {sig.label}
                  </span>
                  {trade.latest_signal_reason && trade.latest_signal && trade.latest_signal !== "hold" && (
                    <div style={{
                      fontSize: 10, color: sig.color, marginTop: 4, lineHeight: 1.3,
                      opacity: 0.8, fontFamily: "monospace",
                    }}>
                      {trade.latest_signal_reason}
                    </div>
                  )}
                  {trade.suggested_close_qty && trade.quantity > 1 && trade.suggested_close_qty < trade.quantity && (
                    <div style={{
                      fontSize: 9, color: "#60a5fa", marginTop: 4, lineHeight: 1.3,
                      fontFamily: "monospace", fontStyle: "italic",
                    }}>
                      Suggested: close {trade.suggested_close_qty} of {trade.quantity}
                    </div>
                  )}
                </td>
                {showActions && (
                  <td style={COL_STYLE}>
                    <ActionsDropdown trade={trade} onAction={onAction} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── StocksTable ──────────────────────────────────────────────────────────

interface StocksTableProps {
  holdings: StockHolding[];
  showActions: boolean;
  onSell: (h: StockHolding) => void;
  onEdit: (h: StockHolding) => void;
  onDelete: (h: StockHolding) => void;
}

function StocksTable({ holdings, showActions, onSell, onEdit, onDelete }: StocksTableProps) {
  if (holdings.length === 0) return null;

  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Category", "Source", "Qty", "Avg Price", "Current", "Value", "P&L", "P&L%", "Signal", ...(showActions ? ["Actions"] : [])].map((h) => (
              <th key={h} style={{
                ...COL_STYLE,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: RIGHT_COLS.has(h) ? "right" : "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => {
            const isFlagged = h.signal === "sell" || h.signal === "strong_sell";
            const cp = h.current_price ?? h.buy_price;
            const pnl = (cp - h.buy_price) * h.quantity;
            const pnlPct = h.buy_price > 0 ? ((cp - h.buy_price) / h.buy_price) * 100 : 0;
            const catConfig = CATEGORY_CONFIG[h.category as StockPickCategory];
            const sig = stockSignalConfig(h.signal);

            const rowBg = isFlagged ? "#150808" : i % 2 === 0 ? "#0a1628" : "#080f1e";

            return (
              <tr
                key={h.id}
                style={{ background: rowBg }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
              >
                <td style={COL_STYLE}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {h.ticker}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ fontSize: 10, color: catConfig?.color ?? "#475569", fontFamily: "monospace", fontWeight: 600 }}>
                    {catConfig?.label ?? h.category}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <SourceBadge isAgent={!!h.is_agent_pick} />
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>{h.quantity}</span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    ${h.buy_price.toFixed(2)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    ${cp.toFixed(2)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                    ${(cp * h.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{
                    color: pnlColor(pnl), fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13,
                  }}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(0)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{
                    color: pnlColor(pnlPct), fontFamily: "'Space Mono', monospace", fontSize: 12,
                  }}>
                    {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5, background: sig.bg, color: sig.color,
                  }}>
                    {sig.label}
                  </span>
                </td>
                {showActions && (
                  <td style={COL_STYLE}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => onEdit(h)} title="Edit" style={{
                        padding: "5px 8px", borderRadius: 6, border: "1px solid #854d0e50",
                        background: "#854d0e15", color: "#facc15", fontSize: 11, fontWeight: 600,
                        cursor: "pointer", display: "flex", alignItems: "center",
                      }}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => onDelete(h)} title="Delete" style={{
                        padding: "5px 8px", borderRadius: 6, border: "1px solid #47556950",
                        background: "#47556915", color: "#64748b", fontSize: 11, fontWeight: 600,
                        cursor: "pointer", display: "flex", alignItems: "center",
                      }}>
                        <Trash2 size={12} />
                      </button>
                      <button onClick={() => onSell(h)} style={{
                        padding: "5px 12px", borderRadius: 6, border: "1px solid #7f1d1d50",
                        background: "#7f1d1d15", color: "#f87171", fontSize: 11, fontWeight: 600,
                        cursor: "pointer", fontFamily: "monospace",
                      }}>
                        SELL
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── ClosedTradesTable ────────────────────────────────────────────────────────

function ClosedOptionsTable({ trades }: { trades: Trade[] }) {
  if (trades.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Strategy", "Source", "Qty", "Strikes", "Expiry", "Opened", "Closed", "Credit", "P&L", "Result"].map((h) => (
              <th key={h} style={{
                ...COL_STYLE,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: RIGHT_COLS.has(h) ? "right" : "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((trade, i) => {
            const sc = statusConfig(trade.status);
            const pnl = trade.realized_pnl;
            const pnlDisplay = pnl != null
              ? `${pnl >= 0 ? "+" : ""}$${pnl.toFixed(0)}`
              : "—";

            return (
              <tr
                key={trade.id}
                style={{ background: i % 2 === 0 ? "#0a1628" : "#080f1e" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#0a1628" : "#080f1e")}
              >
                <td style={COL_STYLE}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {trade.ticker}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#94a3b8" }}>{formatStrategy(trade.strategy)}</span>
                </td>
                <td style={COL_STYLE}>
                  <SourceBadge isAgent={!!trade.is_agent_recommended} />
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    {trade.quantity}
                    {trade.parent_trade_id && (
                      <span style={{ color: "#60a5fa", fontSize: 9, marginLeft: 3 }}>PARTIAL</span>
                    )}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    {formatStrikes(trade)}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#64748b" }}>{formatExpiry(trade.short_expiry)}</span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#475569" }}>{formatDate(trade.opened_at)}</span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#475569" }}>{formatDate(trade.closed_at)}</span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#4ade80", fontFamily: "'Space Mono', monospace" }}>
                    ${trade.net_credit.toFixed(2)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{
                    color: pnlColor(pnl),
                    fontFamily: "'Space Mono', monospace",
                    fontWeight: 700, fontSize: 13,
                  }}>
                    {pnlDisplay}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5, color: sc.color,
                    background: sc.color === "#4ade80" ? "#052e16" : sc.color === "#f87171" ? "#1a0a0a" : "#0d1526",
                  }}>
                    {sc.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── SoldStocksTable ──────────────────────────────────────────────────────────

function SoldStocksTable({ holdings }: { holdings: StockHolding[] }) {
  if (holdings.length === 0) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #1e293b" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #1e293b" }}>
            {["Ticker", "Category", "Source", "Qty", "Buy", "Sell", "P&L", "Return", "Sold", "Result"].map((h) => (
              <th key={h} style={{
                ...COL_STYLE,
                color: "#475569", fontSize: 10, letterSpacing: 1, fontWeight: 600,
                background: "#0d1526", textAlign: RIGHT_COLS.has(h) ? "right" : "left",
                borderBottom: "1px solid #1e293b",
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {holdings.map((h, i) => {
            const pnl = h.realized_pnl ?? 0;
            const pnlPct = h.realized_pnl_pct ?? 0;
            const isProfit = pnl >= 0;
            const sc = isProfit
              ? { label: "PROFIT", bg: "#052e16", color: "#4ade80" }
              : { label: "LOSS", bg: "#1a0a0a", color: "#f87171" };

            return (
              <tr
                key={h.id}
                style={{ background: i % 2 === 0 ? "#0a1628" : "#080f1e" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#0d1526")}
                onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "#0a1628" : "#080f1e")}
              >
                <td style={COL_STYLE}>
                  <span style={{ color: "#f1f5f9", fontWeight: 700, fontFamily: "'Space Mono', monospace", fontSize: 13 }}>
                    {h.ticker}
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#94a3b8", textTransform: "capitalize" }}>{h.category.replace(/_/g, " ")}</span>
                </td>
                <td style={COL_STYLE}>
                  <SourceBadge isAgent={!!h.is_agent_pick} />
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>{h.quantity}</span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#94a3b8", fontFamily: "'Space Mono', monospace" }}>
                    ${h.buy_price.toFixed(2)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{ color: "#e2e8f0", fontFamily: "'Space Mono', monospace" }}>
                    ${(h.sell_price ?? 0).toFixed(2)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{
                    color: pnlColor(pnl), fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13,
                  }}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(0)}
                  </span>
                </td>
                <td style={COL_RIGHT}>
                  <span style={{
                    color: pnlColor(pnlPct), fontFamily: "'Space Mono', monospace", fontSize: 12,
                  }}>
                    {pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%
                  </span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{ color: "#475569" }}>{formatDate(h.sold_at)}</span>
                </td>
                <td style={COL_STYLE}>
                  <span style={{
                    padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700,
                    letterSpacing: 0.5, background: sc.bg, color: sc.color,
                  }}>
                    {sc.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── TradesPage ────────────────────────────────────────────────────────────

type StatusFilter = "open" | "closed" | "all";
type AssetFilter = "all" | "options" | "stocks";

export default function TradesPage({ subTab, onSubTabChange }: {
  subTab: string | null;
  onSubTabChange: (s: string | null) => void;
}) {
  const assetFilter: AssetFilter = (subTab === "options" || subTab === "stocks") ? subTab : "all";
  function setAssetFilter(f: AssetFilter) { onSubTabChange(f === "all" ? null : f); }

  const [trades, setTrades] = useState<Trade[]>([]);
  const [holdings, setHoldings] = useState<StockHolding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");

  // Modal state
  const [closeTarget, setCloseTarget] = useState<{ trade: Trade; mode: "close" | "expire" } | null>(null);
  const [sellTarget, setSellTarget] = useState<StockHolding | null>(null);
  const [editTarget, setEditTarget] = useState<StockHolding | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StockHolding | null>(null);
  const [showManualAdd, setShowManualAdd] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const stockStatus = statusFilter === "open" ? "holding" : statusFilter === "closed" ? "sold" : "all";
      const [tradeData, holdingData] = await Promise.all([
        fetchTrades(statusFilter),
        fetchStockPortfolio(stockStatus),
      ]);
      setTrades(tradeData);
      setHoldings(holdingData);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load trades");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter]);

  function handleCloseSuccess() {
    setCloseTarget(null);
    load();
  }

  function handleStockAction() {
    setSellTarget(null);
    setEditTarget(null);
    setDeleteTarget(null);
    setShowManualAdd(false);
    load();
  }

  // Filter data by asset type
  const showOptions = assetFilter === "all" || assetFilter === "options";
  const showStocks = assetFilter === "all" || assetFilter === "stocks";

  const filteredTrades = showOptions ? trades : [];
  const filteredHoldings = showStocks ? holdings : [];

  // For open view
  const openTrades = useMemo(() => filteredTrades.filter(t => t.status === "open"), [filteredTrades]);
  const closedTrades = useMemo(() => filteredTrades.filter(t => t.status !== "open"), [filteredTrades]);
  const activeHoldings = useMemo(() => filteredHoldings.filter(h => h.status === "holding"), [filteredHoldings]);
  const soldHoldings = useMemo(() => filteredHoldings.filter(h => h.status !== "holding"), [filteredHoldings]);

  const isOpenView = statusFilter === "open";
  const isClosedView = statusFilter === "closed";
  const isAllView = statusFilter === "all";

  const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
    { key: "open", label: "Open" },
    { key: "closed", label: "Closed" },
    { key: "all", label: "All" },
  ];

  const ASSET_FILTERS: { key: AssetFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "options", label: "Options" },
    { key: "stocks", label: "Stocks" },
  ];

  const hasContent = filteredTrades.length > 0 || filteredHoldings.length > 0;

  return (
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "32px 40px 60px" }}>

      {/* Portfolio bar (open/all view) */}
      {!loading && (isOpenView || isAllView) && (
        <PortfolioBar trades={trades} holdings={holdings.filter(h => h.status === "holding")} />
      )}

      {/* Needs attention banner */}
      {!loading && (isOpenView || isAllView) && (
        <NeedsAttentionBanner
          trades={showOptions ? trades : []}
          holdings={showStocks ? holdings.filter(h => h.status === "holding") : []}
        />
      )}

      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{
          fontSize: 22, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: "#f1f5f9",
        }}>
          Trade Book
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {assetFilter === "stocks" && isOpenView && (
            <button onClick={() => setShowManualAdd(true)} style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "7px 14px", borderRadius: 8, border: "1px solid #1d4ed830",
              background: "#1d4ed810", color: "#60a5fa", fontSize: 11, fontWeight: 600,
              cursor: "pointer", fontFamily: "monospace",
            }}>
              <Plus size={12} />
              ADD STOCK
            </button>
          )}
          <button onClick={() => load()} disabled={loading} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 16px", borderRadius: 8, border: "1px solid #1e293b",
            background: "#0d1526", color: "#64748b", cursor: loading ? "default" : "pointer",
            fontSize: 12, fontFamily: "monospace",
          }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            {loading ? "LOADING..." : "REFRESH"}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        {/* Status filter */}
        <div style={{ display: "flex", gap: 6 }}>
          {STATUS_FILTERS.map(({ key, label }) => (
            <button key={key} onClick={() => setStatusFilter(key)} style={{
              padding: "7px 18px", borderRadius: 20, border: "1px solid",
              borderColor: statusFilter === key ? "#1d4ed8" : "#1e293b",
              background: statusFilter === key ? "#1d4ed820" : "transparent",
              color: statusFilter === key ? "#60a5fa" : "#475569",
              fontSize: 12, cursor: "pointer", fontFamily: "monospace",
            }}>
              {label.toUpperCase()}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 24, background: "#1e293b" }} />

        {/* Asset type filter */}
        <div style={{ display: "flex", gap: 6 }}>
          {ASSET_FILTERS.map(({ key, label }) => (
            <button key={key} onClick={() => setAssetFilter(key)} style={{
              padding: "7px 18px", borderRadius: 20, border: "1px solid",
              borderColor: assetFilter === key ? "#854d0e" : "#1e293b",
              background: assetFilter === key ? "#854d0e20" : "transparent",
              color: assetFilter === key ? "#facc15" : "#475569",
              fontSize: 12, cursor: "pointer", fontFamily: "monospace",
            }}>
              {label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
          padding: 20, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <AlertCircle size={18} color="#f87171" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: "#f87171", fontWeight: 600, marginBottom: 4 }}>Failed to load trades</div>
            <div style={{ color: "#7f1d1d", fontSize: 12, fontFamily: "monospace" }}>{error}</div>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{
              height: 52, background: "#0a1628", borderRadius: 8,
              border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite",
              opacity: 1 - i * 0.15,
            }} />
          ))}
        </div>
      )}

      {/* ── Content ── */}
      {!loading && hasContent && (
        <>
          {/* Open view */}
          {isOpenView && (
            <>
              {showOptions && openTrades.length > 0 && (
                <>
                  {assetFilter === "all" && (
                    <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                      OPTIONS · {openTrades.length} OPEN
                    </div>
                  )}
                  <OptionsTable trades={openTrades} showActions onAction={(t, m) => setCloseTarget({ trade: t, mode: m })} />
                  {showStocks && activeHoldings.length > 0 && <div style={{ height: 28 }} />}
                </>
              )}
              {showStocks && activeHoldings.length > 0 && (
                <>
                  {assetFilter === "all" && (
                    <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                      STOCKS · {activeHoldings.length} HOLDING
                    </div>
                  )}
                  <StocksTable
                    holdings={activeHoldings}
                    showActions
                    onSell={setSellTarget}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                  />
                </>
              )}
            </>
          )}

          {/* Closed view */}
          {isClosedView && (
            <>
              {showOptions && closedTrades.length > 0 && (
                <>
                  {assetFilter === "all" && (
                    <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                      CLOSED OPTIONS · {closedTrades.length}
                    </div>
                  )}
                  <ClosedOptionsTable trades={closedTrades} />
                  {showStocks && soldHoldings.length > 0 && <div style={{ height: 28 }} />}
                </>
              )}
              {showStocks && soldHoldings.length > 0 && (
                <>
                  {assetFilter === "all" && (
                    <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                      SOLD STOCKS · {soldHoldings.length}
                    </div>
                  )}
                  <SoldStocksTable holdings={soldHoldings} />
                </>
              )}
            </>
          )}

          {/* All view */}
          {isAllView && (
            <>
              {showOptions && openTrades.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                    OPEN OPTIONS · {openTrades.length}
                  </div>
                  <OptionsTable trades={openTrades} showActions onAction={(t, m) => setCloseTarget({ trade: t, mode: m })} />
                  <div style={{ height: 28 }} />
                </>
              )}
              {showStocks && activeHoldings.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                    STOCK HOLDINGS · {activeHoldings.length}
                  </div>
                  <StocksTable
                    holdings={activeHoldings}
                    showActions
                    onSell={setSellTarget}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                  />
                  <div style={{ height: 28 }} />
                </>
              )}
              {showOptions && closedTrades.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                    CLOSED OPTIONS · {closedTrades.length}
                  </div>
                  <ClosedOptionsTable trades={closedTrades} />
                  <div style={{ height: 28 }} />
                </>
              )}
              {showStocks && soldHoldings.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#475569", fontFamily: "monospace", letterSpacing: 1, marginBottom: 12 }}>
                    SOLD STOCKS · {soldHoldings.length}
                  </div>
                  <SoldStocksTable holdings={soldHoldings} />
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !hasContent && !error && (
        <div style={{
          textAlign: "center", padding: "80px 0",
          border: "1px dashed #1e293b", borderRadius: 16,
        }}>
          <div style={{ fontSize: 48, marginBottom: 16, color: "#1e293b" }}>+</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#475569", marginBottom: 8 }}>
            No {statusFilter === "open" ? "open" : statusFilter === "closed" ? "closed" : ""} trades
          </div>
          <div style={{ fontSize: 13, color: "#334155" }}>
            {statusFilter === "open"
              ? "Execute a recommendation from Options or Stock Picks to open a position."
              : "No trades in this view yet."}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {closeTarget && (
        <CloseModal
          trade={closeTarget.trade}
          mode={closeTarget.mode}
          onClose={() => setCloseTarget(null)}
          onSuccess={handleCloseSuccess}
        />
      )}
      {sellTarget && (
        <SellModal holding={sellTarget} onClose={() => setSellTarget(null)} onSell={handleStockAction} />
      )}
      {editTarget && (
        <EditHoldingModal holding={editTarget} onClose={() => setEditTarget(null)} onSave={handleStockAction} />
      )}
      {deleteTarget && (
        <DeleteHoldingModal holding={deleteTarget} onClose={() => setDeleteTarget(null)} onDelete={handleStockAction} />
      )}
      {showManualAdd && (
        <ManualAddModal onClose={() => setShowManualAdd(false)} onAdd={handleStockAction} />
      )}
    </main>
  );
}
